import { useEffect, useState } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { TRIBES } from "@/lib/tribes";
import VideoEmbed from "@/components/app/VideoEmbed";
import PracticeCelebrateModal from "@/components/app/PracticeCelebrateModal";
import CheckInReminderModal from "@/components/app/CheckInReminderModal";
import { CheckCircle2, ArrowLeft, Video, Heart, Eye, Lock, GraduationCap, Users } from "lucide-react";

function ExerciseCard({ ex, childId, sessionNumber, tribe, viewed, onViewed }) {
  const [showMod, setShowMod] = useState(false);
  const [markingViewed, setMarkingViewed] = useState(false);

  const markViewed = async () => {
    if (viewed) return;
    setMarkingViewed(true);
    try {
      await api.post(`/sessions/${sessionNumber}/mark-exercise-viewed`, { child_id: childId, exercise_key: ex.key });
      onViewed && onViewed();
    } catch (e) { alert(formatApiError(e)); }
    finally { setMarkingViewed(false); }
  };

  return (
    <div className="rounded-3xl border p-6" style={{ background: tribe.surface, borderColor: tribe.accent + "55" }}>
      <div className="flex items-start justify-between">
        <div>
          <div className="text-xs uppercase tracking-widest font-bold opacity-70" style={{ color: tribe.accent }}>{ex.category}</div>
          <h3 className="font-display text-xl font-bold mt-1" style={{ color: tribe.ink }}>{ex.name}</h3>
          {viewed && (
            <div className="mt-1 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-bold" style={{ color: tribe.accent }} data-testid={`ex-viewed-badge-${ex.name}`}>
              <CheckCircle2 size={11} /> Viewed
            </div>
          )}
        </div>
      </div>

      <div className="mt-4 rounded-2xl p-4" style={{ background: tribe.panel_soft, opacity: 0.95 }}>
        <div className="text-sm leading-relaxed" style={{ color: tribe.ink }}>{ex.instructions}</div>
      </div>

      {ex.video_url ? (
        <div className="mt-3" data-testid={`exercise-video-${ex.name}`}>
          <VideoEmbed value={ex.video_url} title={ex.name} theme={{ borderColor: tribe.accent, background: tribe.panel_soft, color: tribe.ink }} testid={`exercise-video-${ex.name}`} />
        </div>
      ) : (
        <div className="mt-3 flex items-center gap-2 text-xs opacity-60" style={{ color: tribe.ink }}>
          <Video size={12} /> Video will appear here once your admin adds one
        </div>
      )}

      {!viewed && (
        <button
          type="button"
          onClick={markViewed}
          disabled={markingViewed}
          data-testid={`ex-mark-viewed-${ex.name}`}
          className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2 rounded-full text-sm font-semibold border-2 transition"
          style={{ borderColor: tribe.accent, color: tribe.accent, background: "transparent" }}
        >
          <Eye size={14} /> {markingViewed ? "Marking…" : "Mark exercise as viewed"}
        </button>
      )}

      {ex.modification && (
        <div className="mt-4">
          <button
            onClick={() => setShowMod((s) => !s)}
            data-testid={`show-mod-${ex.name}`}
            className="text-xs font-semibold underline"
            style={{ color: tribe.key === "nasal_ninja" ? "#FFFEF9" : "#3F3D56" }}
          >
            {showMod ? "Hide" : "See"} modification exercise ({ex.modification.trigger})
          </button>
          {showMod && (
            <div className="mt-2 p-4 rounded-xl border" style={{ borderColor: tribe.accent }} data-testid={`mod-panel-${ex.name}`}>
              <div className="font-semibold text-sm" style={{ color: tribe.ink }}>{ex.modification.name}</div>
              <div className="text-sm mt-1 opacity-80" style={{ color: tribe.ink }}>{ex.modification.instructions}</div>

              {(ex.modification.revisit && ex.modification.revisit.length > 0) ? (
                <div className="mt-4 space-y-2" data-testid={`mod-revisit-${ex.name}`}>
                  <div className="text-[11px] uppercase tracking-widest font-bold opacity-70" style={{ color: tribe.accent }}>
                    Watch the original exercise
                  </div>
                  {ex.modification.revisit.map((r, i) => (
                    <Link
                      key={i}
                      to={`/child/${childId}/session/${r.session_number}`}
                      data-testid={`mod-revisit-link-${ex.name}-${r.session_number}`}
                      className="flex items-center justify-between gap-3 rounded-xl border px-3 py-2 hover:-translate-y-0.5 transition"
                      style={{ borderColor: tribe.accent, color: tribe.ink }}
                    >
                      <div className="text-sm">
                        <span className="font-semibold">{r.exercise_name}</span>
                        <span className="opacity-70"> · Session {r.session_number}</span>
                      </div>
                      <span className="text-xs opacity-80" style={{ color: tribe.accent }}>Open →</span>
                    </Link>
                  ))}
                </div>
              ) : ex.modification.no_video ? (
                <div className="mt-3 text-xs italic opacity-70" data-testid={`mod-text-only-${ex.name}`} style={{ color: tribe.ink }}>
                  No video for this one — the explanation above is all you need.
                </div>
              ) : (
                <div className="mt-4" data-testid={`mod-video-${ex.name}`}>
                  <VideoEmbed
                    value={ex.modification.video_url || ""}
                    title={`Modification — ${ex.modification.name}`}
                    placeholder={`Modification video — ${ex.modification.name}`}
                    theme={{ borderColor: tribe.accent, background: tribe.panel_soft, color: tribe.ink }}
                    testid={`mod-video-${ex.name}`}
                  />
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function SessionDetail() {
  const { childId, sessionNumber } = useParams();
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  // ?skip=learn is passed by the "Practice today's session" shortcut when Learn isn't
  // complete yet — it drops the parent directly into the Learn/Practice view and
  // bypasses the Start Here intro, which is not what the shortcut is for.
  const skipStartHere = searchParams.get("skip") === "learn";
  const [child, setChild] = useState(null);
  const [session, setSession] = useState(null);
  const [logs, setLogs] = useState([]);
  const [completing, setCompleting] = useState(false);
  const [view, setView] = useState(skipStartHere ? "exercises" : "start_here"); // "start_here" | "exercises"
  const [learnViewed, setLearnViewed] = useState([]); // exercise keys already marked viewed
  const [markingPractice, setMarkingPractice] = useState(false);
  const [justMarked, setJustMarked] = useState(false);
  const [practiceRating, setPracticeRating] = useState("");
  const [practiceNote, setPracticeNote] = useState("");
  // One-time acknowledgment popup for the Myo Check-In. `needsAck=null` means we
  // haven't queried yet; `true` shows the modal; `false` keeps it hidden. `ackSubmitting`
  // prevents double-clicks while the ack is being POSTed to the server.
  const [needsAck, setNeedsAck] = useState(null);
  const [ackSubmitting, setAckSubmitting] = useState(false);

  // Reset view when the session URL changes. Honor ?skip=learn on entry so the
  // shortcut fallback lands on Learn/Practice, not on Start Here.
  useEffect(() => { setView(skipStartHere ? "exercises" : "start_here"); }, [childId, sessionNumber, skipStartHere]);

  const load = async () => {
    const [c, s, l, lp] = await Promise.all([
      api.get(`/children/${childId}`),
      api.get(`/sessions/${sessionNumber}`),
      api.get(`/exercise-logs/${childId}?session_number=${sessionNumber}`),
      api.get(`/sessions/${sessionNumber}/learn-progress?child_id=${childId}`),
    ]);
    setChild(c.data); setSession(s.data); setLogs(l.data); setLearnViewed(lp.data.viewed || []);
  };
  useEffect(() => { load(); }, [childId, sessionNumber]);

  // Ask the server whether the parent has already been shown / acknowledged the
  // Myo Check-In reminder for this session. If not, we render the non-dismissible
  // modal. Failure is silent — we'd rather skip the popup than block session access.
  useEffect(() => {
    let alive = true;
    setNeedsAck(null);
    api.get(`/child/${childId}/session/${sessionNumber}/checkin-reminder-status`)
      .then((r) => { if (alive) setNeedsAck(!!r.data?.needs_ack); })
      .catch(() => { if (alive) setNeedsAck(false); });
    return () => { alive = false; };
  }, [childId, sessionNumber]);

  const recordAck = async (action) => {
    // action: "fill_now" | "later" — persist the parent's choice for the audit trail.
    setAckSubmitting(true);
    try {
      await api.post(`/child/${childId}/session/${sessionNumber}/checkin-reminder-ack`, { action });
    } catch (_) {
      // Non-blocking — the parent should never be stuck here. The persistent banner
      // continues to nudge them regardless of whether the log write succeeded.
    } finally {
      setAckSubmitting(false);
      setNeedsAck(false);
    }
  };
  const onFillNow = async () => { await recordAck("fill_now"); nav(`/child/${childId}/checkin`); };
  const onLater = async () => { await recordAck("later"); };

  const markComplete = async () => {
    setCompleting(true);
    try {
      await api.post("/sessions/complete", { child_id: childId, session_number: parseInt(sessionNumber, 10) });
      if (parseInt(sessionNumber, 10) === 12) nav(`/child/${childId}/graduation`);
      else nav(`/child/${childId}/certificate/${sessionNumber}`);
    } catch (e) { alert(formatApiError(e)); setCompleting(false); }
  };

  const markPractice = async () => {
    setMarkingPractice(true); setJustMarked(false);
    try {
      await api.post(`/sessions/${sessionNumber}/practice-log`, {
        child_id: childId,
        rating: practiceRating || null,
        note: practiceNote,
      });
      setJustMarked(true);
      setPracticeRating("");
      setPracticeNote("");
      await load();
      // Modal handles its own auto-dismiss so we don't fight it here.
    } catch (e) { alert(formatApiError(e)); }
    finally { setMarkingPractice(false); }
  };

  if (!child || !session) return <div className="min-h-screen shell-bg p-8">Loading…</div>;
  const tribe = TRIBES[child.tribe] || TRIBES.neutral;
  const canComplete = child.current_session === session.number;
  const target = 28;
  const done = logs.length;
  const MIN = 3;
  const unlocksAt = child.session_unlocks_at ? new Date(child.session_unlocks_at) : null;
  const now = new Date();
  const daysUntilUnlock = unlocksAt ? Math.max(0, Math.ceil((unlocksAt - now) / (86400000))) : 0;
  const gateOpen = unlocksAt ? unlocksAt <= now : true;
  const practiceOk = done >= MIN;
  const canMarkComplete = canComplete && gateOpen && practiceOk;

  return (
    <div className={`min-h-screen tribe-${child.tribe || "neutral"}`}>
      <div className="max-w-4xl mx-auto px-4 py-8">
        <Link to={`/child/${childId}`} className="inline-flex items-center gap-2 text-sm opacity-70 hover:opacity-100 mb-6" style={{ color: tribe.ink }}>
          <ArrowLeft size={14} /> Back to {child.is_self ? "your journey" : `${child.name}'s journey`}
        </Link>
        <div className="mb-8">
          <div className="text-xs uppercase tracking-widest font-bold" style={{ color: tribe.accent }}>{child.is_self ? "Your journey · " : (child.tribe === "neutral" ? "" : tribe.name + " · ")}Session {session.number} of 12</div>
          <h1 className="font-display text-4xl md:text-5xl font-bold mt-2" style={{ color: tribe.ink }}>{session.title}</h1>
          <p className="mt-2 opacity-80" style={{ color: tribe.ink }}>{session.focus}</p>
        </div>

        {view === "start_here" && (
          <div className="rounded-3xl p-6 md:p-10" style={{ background: tribe.panel, border: `2px solid ${tribe.accent}` }} data-testid="start-here">
            <div className="text-xs uppercase tracking-[0.3em] font-bold" style={{ color: tribe.accent }}>Session {session.number} · Start Here</div>
            <div className="font-display text-2xl md:text-3xl font-bold mt-2" style={{ color: tribe.ink }}>{session.title}</div>
            <div className="mt-6" data-testid="start-here-video">
              <VideoEmbed
                value={session.start_here?.video_url}
                title={`Session ${session.number} intro`}
                placeholder={`Session ${session.number} intro — what to expect this week`}
                theme={{ borderColor: tribe.accent, background: tribe.panel_soft, color: tribe.ink }}
                testid="start-here-video"
              />
            </div>
            {session.start_here?.summary && (
              <p className="mt-6 leading-relaxed opacity-95 text-base" style={{ color: tribe.ink }} data-testid="start-here-summary">{session.start_here.summary}</p>
            )}
            <div className="mt-8 flex flex-wrap gap-3 items-center">
              <button data-testid="start-here-continue" onClick={() => setView("exercises")} className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold" style={{ background: tribe.accent, color: "#1E1E2E" }}>
                Continue to exercises <ArrowLeft size={14} style={{ transform: "rotate(180deg)" }} />
              </button>
              <Link to="/fell-off" data-testid="session-fell-off-btn" className="inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full border" style={{ borderColor: tribe.accent, color: tribe.ink }}>
                <Heart size={12} /> Fell off track?
              </Link>
            </div>
          </div>
        )}

        {view === "exercises" && (() => {
          const allExercises = [...(session.active || []), ...(session.passive || [])];
          const totalEx = allExercises.length;
          const viewedCount = allExercises.filter((e) => learnViewed.includes(e.key)).length;
          const learnComplete = totalEx > 0 && viewedCount >= totalEx;
          return (
          <>
        <button onClick={() => setView("start_here")} data-testid="back-to-start-here" className="mb-6 inline-flex items-center gap-2 text-xs font-semibold px-3 py-1.5 rounded-full border" style={{ borderColor: tribe.accent, color: tribe.ink }}>
          <ArrowLeft size={12} /> Back to Start Here
        </button>

        <div className="mb-8 p-4 rounded-2xl" style={{ background: tribe.panel, border: `1px solid ${tribe.accent}` }}>
          <div className="flex items-center justify-between text-sm">
            <span style={{ color: tribe.ink }}><b>{done}</b> practices logged · aim for {target} across 2 weeks</span>
            <span className="italic opacity-70" style={{ color: tribe.ink }}>progress, not perfection</span>
          </div>
          <div className="mt-2 h-2 rounded-full" style={{ background: "#E0D8E8" }}>
            <div className="h-full rounded-full" style={{ width: `${Math.min(100, (done / target) * 100)}%`, background: tribe.accent }} />
          </div>
        </div>

        {/* --- 1. Learn the Exercises --- */}
        <div className="mb-10" data-testid="section-learn">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <div className="flex items-center gap-2">
              <GraduationCap size={16} style={{ color: tribe.accent }} />
              <div className="font-display text-lg font-bold" style={{ color: tribe.ink }}>1 · Learn the exercises</div>
            </div>
            <div className="text-xs opacity-80 inline-flex items-center gap-2" style={{ color: tribe.ink }} data-testid="learn-progress-badge">
              <span>{viewedCount} of {totalEx} viewed</span>
              {learnComplete && <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full font-semibold" style={{ background: tribe.accent, color: tribe.key === "nasal_ninja" ? "#0B0B22" : "#1E1E2E" }}><CheckCircle2 size={11} /> Complete</span>}
            </div>
          </div>
          <p className="text-xs opacity-70 mb-4" style={{ color: tribe.ink }}>
            {learnComplete
              ? "Learn is complete for this session — this section stays here as a permanent reference. Practice with Chelcie is unlocked below."
              : "Watch each exercise video, read the instructions, and tap Mark exercise as viewed. Once all are viewed, Practice with Chelcie unlocks."}
          </p>
          <div className="mb-6">
            <div className="text-xs uppercase tracking-widest font-bold opacity-70 mb-3" style={{ color: tribe.accent }}>Active exercises</div>
            <div className="grid gap-4">
              {session.active.map((ex) => <ExerciseCard key={ex.name} ex={ex} childId={childId} sessionNumber={parseInt(sessionNumber, 10)} tribe={tribe} viewed={learnViewed.includes(ex.key)} onViewed={load} />)}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest font-bold opacity-70 mb-3" style={{ color: tribe.accent }}>Passive habits</div>
            <div className="grid gap-4">
              {session.passive.map((ex) => <ExerciseCard key={ex.name} ex={ex} childId={childId} sessionNumber={parseInt(sessionNumber, 10)} tribe={tribe} viewed={learnViewed.includes(ex.key)} onViewed={load} />)}
            </div>
          </div>
        </div>

        {/* --- 2. Practice with Chelcie --- */}
        <div className="mb-10" data-testid="section-practice">
          <div className="flex items-center gap-2 mb-3">
            <Users size={16} style={{ color: tribe.accent }} />
            <div className="font-display text-lg font-bold" style={{ color: tribe.ink }}>2 · Practice with Chelcie</div>
          </div>
          {learnComplete ? (
            <div className="rounded-3xl p-6 md:p-8" style={{ background: tribe.panel, border: `2px solid ${tribe.accent}` }}>
              <p className="text-sm opacity-80 mb-4" style={{ color: tribe.ink }}>
                A single timed video that walks the whole family through this session's full set of exercises in sequence. Practice along with Chelcie, or use it as a quiet reminder — either counts.
              </p>
              <VideoEmbed
                value={session.practice_with_chelcie?.video_url}
                title={`Practice with Chelcie — Session ${session.number}`}
                placeholder={`Practice with Chelcie · Session ${session.number} — combined timed practice`}
                theme={{ borderColor: tribe.accent, background: tribe.panel_soft, color: tribe.ink }}
                testid="practice-with-chelcie-video"
              />
              {/* --- 3. Mark Practice Complete (independent of watching) --- */}
              <div className="mt-6 rounded-2xl p-5" style={{ background: tribe.panel_soft, border: `1px dashed ${tribe.accent}` }} data-testid="mark-practice-block">
                <div className="mb-3">
                  <div className="text-sm font-semibold" style={{ color: tribe.ink }}>Practiced today?</div>
                  <div className="text-xs opacity-80 mt-0.5" style={{ color: tribe.ink }}>Log one practice for this session — it counts toward the {target} target, whether or not you watched the video.</div>
                </div>

                <div className="text-xs uppercase tracking-widest font-semibold mb-2" style={{ color: tribe.ink }}>How did that feel?</div>
                <div className="flex gap-2 flex-wrap">
                  {["easy", "okay", "hard"].map((r) => (
                    <button
                      key={r}
                      type="button"
                      onClick={() => setPracticeRating(r)}
                      data-testid={`practice-rate-${r}`}
                      className="px-4 py-2 rounded-full text-sm font-semibold border-2 transition"
                      style={practiceRating === r
                        ? { background: tribe.accent, borderColor: tribe.accent, color: tribe.key === "nasal_ninja" ? "#0B0B22" : "#1E1E2E" }
                        : { borderColor: tribe.accent, color: tribe.ink, background: "transparent" }}
                    >
                      {r}
                    </button>
                  ))}
                </div>

                <textarea
                  placeholder="Optional note…"
                  value={practiceNote}
                  onChange={(e) => setPracticeNote(e.target.value)}
                  data-testid="practice-note"
                  rows={2}
                  className="mt-3 w-full px-3 py-2 rounded-xl text-sm border-2"
                  style={{ background: tribe.key === "nasal_ninja" ? "#0F1020" : "#FFFEF9", color: tribe.ink, borderColor: tribe.accent }}
                />

                <button
                  onClick={markPractice}
                  disabled={markingPractice}
                  data-testid="mark-practice-complete-btn"
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-5 py-2.5 rounded-full font-semibold shrink-0 disabled:opacity-40"
                  style={{ background: tribe.accent, color: tribe.key === "nasal_ninja" ? "#0B0B22" : "#1E1E2E" }}
                >
                  <CheckCircle2 size={16} /> {markingPractice ? "Logging…" : "Mark practice complete"}
                </button>
              </div>

            </div>
          ) : (
            <div className="rounded-3xl p-6 md:p-8 opacity-90 text-center" style={{ background: tribe.panel_soft, border: `2px dashed ${tribe.accent}` }} data-testid="practice-locked">
              <Lock size={22} className="mx-auto opacity-60" style={{ color: tribe.ink }} />
              <div className="mt-2 font-display text-lg font-bold" style={{ color: tribe.ink }}>Locked until you finish Learn the Exercises</div>
              <div className="mt-1 text-sm opacity-80" style={{ color: tribe.ink }}>
                Mark all {totalEx} exercises above as viewed — {totalEx - viewedCount} to go — and this practice video opens. This reminder only appears the first time you're in this session.
              </div>
            </div>
          )}
        </div>

        {canComplete && (
          <div className="p-6 rounded-3xl border-2" style={{ borderColor: tribe.accent, background: tribe.panel }}>
            <div className="font-display text-xl font-bold" style={{ color: tribe.ink }}>Mark session {session.number} complete</div>
            <div className="text-sm mt-2 opacity-80" style={{ color: tribe.ink }}>
              {session.number >= 12
                ? "You'll earn your graduation certificate the moment you mark this session complete."
                : `You'll earn a certificate and unlock session ${session.number + 1} fourteen days after you start this session.`}
              {" "}After completing, upload a photo with the certificate as proof of practice — that's when the $1 donation is credited.
            </div>
            <div className="mt-4 text-sm space-y-1" style={{ color: tribe.ink }}>
              <div className="flex items-center gap-2">
                <span style={practiceOk ? { color: tribe.accent } : { opacity: 0.6 }}>{practiceOk ? "✓" : "○"}</span>
                <span>Practices logged: {done} / {target} recommended</span>
              </div>
              <div className="flex items-center gap-2">
                <span style={gateOpen ? { color: tribe.accent } : { opacity: 0.6 }}>{gateOpen ? "✓" : "○"}</span>
                <span>{gateOpen
                  ? (session.number >= 12 ? "14-day pacing complete" : `Session ${session.number + 1} is ready to open`)
                  : `Session ${Math.min(12, session.number + 1)} opens in ${daysUntilUnlock} more day${daysUntilUnlock === 1 ? "" : "s"}`}</span>
              </div>
            </div>
            <button data-testid="complete-session-btn" onClick={markComplete} disabled={completing || !canMarkComplete} className="mt-4 inline-flex items-center gap-2 px-6 py-3 rounded-full font-semibold disabled:opacity-40" style={{ background: tribe.accent, color: "#1E1E2E" }}>
              <CheckCircle2 size={16} /> {completing ? "Saving…" : "Mark complete"}
            </button>
          </div>
        )}
          </>
          );
        })()}
      </div>
      <PracticeCelebrateModal
        open={justMarked}
        onClose={() => setJustMarked(false)}
        childName={child?.is_self ? "" : child?.name}
        tribe={tribe}
        practicesDone={done}
        target={target}
        nextSession={Math.min(12, session.number + 1)}
      />
      <CheckInReminderModal
        open={needsAck === true}
        sessionNumber={session.number}
        tribe={tribe}
        onFillNow={onFillNow}
        onLater={onLater}
        submitting={ackSubmitting}
      />
    </div>
  );
}
